import fetcher from 'api/fetcher';
import { MagritteNotificationName } from 'utils/constants/notification';
import { NEXT_WEEK_FORMATTED, TODAY_FORMATTED } from 'utils/date';

import multicreationNextButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_next_button_click';

import urlParser from 'bloko/common/urlParser';

import { BiddingStrategy } from 'store/models/auctionData';
import {
    AuctionsToCreate,
    MultiCreationAuctionSteps,
    resetMultiCreationFilters,
    setMultiCreationActiveStep,
    setMultiCreationBiddingStrategy,
    setMultiCreationSelectedVacancyIds,
    setMultiCreationVacanciesAuctionInfo,
    setMultiCreationVacanciesSearchValue,
    VacanciesAuctionInfo,
} from 'store/models/multiCreation';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

type FetchVacanciesAuctionInfo = (
    vacancyIds: Array<string>,
    setIsLoading: (loading: boolean) => void
) => AppThunkAction;

export const fetchVacanciesAuctionInfo: FetchVacanciesAuctionInfo = (vacancyIds, setIsLoading) => {
    return (dispatch, getState) => {
        setIsLoading(true);
        const savedVacanciesAuctionInfo = getState().multiCreation.vacanciesAuctionInfo;
        const savedAuctionsToCreate = getState().multiCreation.auctionsToCreate;
        const biddingStrategy = getState().multiCreation.biddingStrategy;
        const isVacanciesSelectStep =
            getState().multiCreation.activeStep === MultiCreationAuctionSteps.VacanciesSelectStep;
        const isDynamicPriceEnabled = getState().user.user?.currentAccount.dynamicPriceEnabled;

        const queryStr = urlParser.stringify({
            vacancyId: vacancyIds,
        });

        return fetcher
            .get<unknown, { vacancyAuctionInfo: VacanciesAuctionInfo }>(
                `/api/v1/auction/vacancy/data/batch/?${queryStr}`
            )
            .then(({ vacancyAuctionInfo }) => {
                const auctionsToCreate: AuctionsToCreate = {};
                const startAt =
                    biddingStrategy === BiddingStrategy.Auto && isDynamicPriceEnabled ? TODAY_FORMATTED : null;
                const endAt =
                    biddingStrategy === BiddingStrategy.Auto && isDynamicPriceEnabled ? NEXT_WEEK_FORMATTED : null;

                Object.keys(vacancyAuctionInfo).forEach((key) => {
                    const vacancyId = Number(key);
                    const { bid, bidRating } = vacancyAuctionInfo[vacancyId];
                    const { disabled, value: bidValue } = bid;
                    let oftenThresholdBid = bidRating.oftenThresholdBid;
                    // для случаев, когда минимальная ставка может быть выше дефолтной
                    if (isDynamicPriceEnabled && bid.minValue < oftenThresholdBid) {
                        oftenThresholdBid = bid.minValue;
                    }

                    auctionsToCreate[vacancyId] = {
                        bid: String(disabled ? bidValue : oftenThresholdBid),
                        minBidValue: bid.minValue,
                        bidValidationError: null,
                        bidFrequencyStatus: bidRating.bidFrequencyStatus,
                        budget: String(bidRating.recommendedBudget),
                        budgetValidationError: null,
                        vacancyId,
                        isCreationFailed: false,
                        startAt,
                        startAtValidationError: null,
                        endAt,
                        endAtValidationError: null,
                    };
                });

                multicreationNextButtonClick({
                    currentStep: MultiCreationAuctionSteps.VacanciesSelectStep,
                    vacancyIds: vacancyIds.toString(),
                });

                const selectedVacancyIds = Object.fromEntries(
                    Object.entries(Object.assign({}, savedVacanciesAuctionInfo, vacancyAuctionInfo)).map(([key]) => [
                        key,
                        true,
                    ])
                );

                dispatch([
                    setMultiCreationVacanciesSearchValue(''),
                    resetMultiCreationFilters(null),
                    setMultiCreationSelectedVacancyIds(selectedVacancyIds),
                    setMultiCreationVacanciesAuctionInfo({
                        vacanciesAuctionInfo: Object.assign({}, savedVacanciesAuctionInfo, vacancyAuctionInfo),
                        auctionsToCreate: Object.assign({}, savedAuctionsToCreate, auctionsToCreate),
                    }),
                    ...(isDynamicPriceEnabled && isVacanciesSelectStep
                        ? [setMultiCreationActiveStep(MultiCreationAuctionSteps.BiddingStrategySelectStep)]
                        : []),
                    ...(!isDynamicPriceEnabled && isVacanciesSelectStep
                        ? [
                              setMultiCreationBiddingStrategy(BiddingStrategy.Manual),
                              setMultiCreationActiveStep(MultiCreationAuctionSteps.BidsAndBudgetsStep),
                          ]
                        : []),
                ]);
            })
            .catch((error) => {
                multicreationNextButtonClick({
                    currentStep: MultiCreationAuctionSteps.VacanciesSelectStep,
                    vacancyIds: vacancyIds.toString(),
                    errors: JSON.stringify(error),
                });
                dispatch(addMagritteNotification(MagritteNotificationName.MultiCreationAuctionUnknownError));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
};
